import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import OrganizationLayout from './OrganizationLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { monthlyCost } from '../../utils/orgPricing';

/*
 * OrganizationMembersPage — Phase 3 real surface.
 *
 * Owner/Admin only sees the invite + role/remove controls. Members see the
 * roster read-only. Wraps a responsive max-width container so the same
 * page renders well on both phone and laptop (Phase 4.5 will style it
 * further; the current layout is already usable on either device).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabel = (role) => {
  if (role === 'owner') return 'Owner';
  if (role === 'admin') return 'Admin';
  return 'Member';
};

const InvitePricePreview = ({ org, pending }) => {
  const now = monthlyCost(org.seat_count);
  const after = monthlyCost((org.seat_count || 1) + pending + 1);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-sm text-indigo-900" data-testid="invite-price-preview">
      <span>Now <strong data-testid="invite-price-now">${now}/month</strong></span>
      <span aria-hidden="true">→</span>
      <span><strong data-testid="invite-price-after">${after}/month</strong> once this teammate joins</span>
      {after === now && <span className="text-emerald-700">(included in your plan)</span>}
      {pending > 0 && <span className="w-full text-xs text-indigo-700/80">Counts your {pending} pending invitation{pending === 1 ? '' : 's'} too.</span>}
      {org.subscription_status === 'trialing' && <span className="w-full text-xs text-indigo-700/80">You won't be charged until your free trial ends.</span>}
    </div>
  );
};

const OrganizationMembersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const ttx = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteSaving, setInviteSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw).id : null;
    } catch { return null; }
  })();
  const myRole = members.find((m) => m.user_id === currentUserId)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const loadAll = useCallback(async (orgId) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const [memRes, invRes] = await Promise.all([
      axios.get(`${API}/organizations/${orgId}/members`, { headers }),
      canManage
        ? axios.get(`${API}/organizations/${orgId}/invitations`, { headers })
        : Promise.resolve({ data: [] }),
    ]).catch(() => [{ data: [] }, { data: [] }]);
    setMembers(memRes.data || []);
    setInvitations(invRes.data || []);
  }, [canManage]);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams(location.search);
        const orgId = params.get('org_id');
        const orgsRes = await axios.get(`${API}/organizations/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = orgsRes.data || [];
        const picked = orgId ? list.find((o) => o.id === orgId) : list[0];
        if (!picked) { setLoading(false); return; }
        setOrg(picked);
        // Initial member fetch — we need to know my role before we know if
        // we can also fetch invitations, so do members first then invites.
        const memRes = await axios.get(
          `${API}/organizations/${picked.id}/members`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMembers(memRes.data || []);
        const mine = (memRes.data || []).find((m) => m.user_id === (
          (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; } })()
        ));
        if (mine && (mine.role === 'owner' || mine.role === 'admin')) {
          try {
            const invRes = await axios.get(
              `${API}/organizations/${picked.id}/invitations`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setInvitations(invRes.data || []);
          } catch { /* ignore */ }
        }
      } catch (e) {
        console.error('Failed to load org members:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [location.search]);

  const handleInvite = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!org || !inviteEmail || inviteSaving) return;
    setInviteSaving(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/organizations/${org.id}/invitations`,
        { email: inviteEmail.trim().toLowerCase(), role: inviteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteEmail('');
      setActionMsg({ kind: 'success', text: 'Invitation sent.' });
      await loadAll(org.id);
    } catch (err) {
      setActionMsg({ kind: 'error', text: err?.response?.data?.detail || 'Could not send the invitation.' });
    } finally {
      setInviteSaving(false);
    }
  }, [org, inviteEmail, inviteRole, inviteSaving, loadAll]);

  const handleRevoke = useCallback(async (invitationId) => {
    if (!org) return;
    if (!window.confirm('Revoke this invitation?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/organizations/${org.id}/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadAll(org.id);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not revoke.');
    }
  }, [org, loadAll]);

  const handleResend = useCallback(async (invitationId) => {
    if (!org) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/organizations/${org.id}/invitations/${invitationId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMsg({ kind: 'success', text: 'Invitation resent.' });
      await loadAll(org.id);
    } catch (err) {
      setActionMsg({ kind: 'error', text: err?.response?.data?.detail || 'Could not resend the invitation.' });
    }
  }, [org, loadAll]);

  const handleChangeRole = useCallback(async (memberId, newRole) => {
    if (!org) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/organizations/${org.id}/members/${memberId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAll(org.id);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not change role.');
    }
  }, [org, loadAll]);

  const handleRemoveMember = useCallback(async (memberId) => {
    if (!org) return;
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/organizations/${org.id}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadAll(org.id);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not remove the member.');
    }
  }, [org, loadAll]);

  // Per-member notification toggle. Optimistically updates the row so
  // there's no perceptible lag, then reconciles with the server response.
  const handleToggleNotification = useCallback(async (memberId, key, nextValue) => {
    if (!org) return;
    setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, [key]: nextValue } : m)));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/organizations/${org.id}/members/${memberId}/notifications`,
        { [key]: nextValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reconcile with server truth — in particular `task_reminders_enabled`
      // defaults to True so the server may return a different shape than
      // the optimistic write if the user record was missing the field.
      const serverState = res.data || {};
      setMembers((prev) => prev.map((m) => {
        if (m.id !== memberId) return m;
        return {
          ...m,
          daily_digest_enabled: typeof serverState.daily_digest_enabled === 'boolean' ? serverState.daily_digest_enabled : m.daily_digest_enabled,
          weekly_digest_enabled: typeof serverState.weekly_digest_enabled === 'boolean' ? serverState.weekly_digest_enabled : m.weekly_digest_enabled,
          task_reminders_enabled: typeof serverState.task_reminders_enabled === 'boolean' ? serverState.task_reminders_enabled : m.task_reminders_enabled,
        };
      }));
    } catch (err) {
      // Roll back on error.
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, [key]: !nextValue } : m)));
      alert(err?.response?.data?.detail || 'Could not update notification.');
    }
  }, [org]);

  const handleToggleContactsAccess = useCallback(async (memberId, nextValue) => {
    setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, can_view_contacts: nextValue } : m)));
    try {
      await axios.put(
        `${API}/organizations/${org.id}/members/${memberId}/contacts-access`,
        { can_view_contacts: nextValue },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
    } catch (err) {
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, can_view_contacts: !nextValue } : m)));
      alert(err?.response?.data?.detail || 'Could not update contacts access.');
    }
  }, [org]);

  if (loading) return null;
  if (!org) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-6 max-w-md w-full text-center">
          <p className="text-gray-700 mb-3">You're not part of any team workspace yet.</p>
          <button onClick={() => navigate('/settings/subscription')} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium">
            Create one
          </button>
        </div>
      </div>
    );
  }

  return (
    <OrganizationLayout org={org} active="members" canManage={canManage}>
      <div data-testid="org-members-page">

        {canManage && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{ttx('inviteTeammate', 'Invite a teammate')}</h2>
            <p className="text-sm text-gray-600 mb-3" data-testid="invite-free-copy">
              {ttx('inviteFreeCopy', "Your teammate joins for free — you cover their seat. Your $10/month plan includes you + 1 teammate; each extra teammate adds $5/month when they accept.")}
            </p>
            <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviteSaving}
                data-testid="invite-email-input"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                disabled={inviteSaving}
                data-testid="invite-role-select"
                className="border border-gray-300 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-indigo-500"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={inviteSaving}
                data-testid="invite-submit"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-60 text-white px-4 py-2 rounded-lg font-medium"
              >
                {inviteSaving ? 'Sending…' : 'Invite'}
              </button>
            </form>
            <InvitePricePreview org={org} pending={invitations.length} />
            {actionMsg && (
              <p className={`text-sm mt-2 ${actionMsg.kind === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                {actionMsg.text}
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Members <span className="text-sm text-gray-500 font-normal">· {members.length}</span>
          </h2>
          <div className="divide-y divide-gray-100">
            {members.map((m) => {
              const isYou = m.user_id === currentUserId;
              const canEditThisRow = canManage && m.role !== 'owner' && !isYou;
              return (
                <div key={m.id} className="py-3" data-testid={`member-row-${m.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {m.name || m.email || m.user_id}
                        {isYou && <span className="ml-1 text-xs text-gray-500">(you)</span>}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canEditThisRow ? (
                        <select
                          value={m.role}
                          onChange={(e) => handleChangeRole(m.id, e.target.value)}
                          data-testid={`member-role-select-${m.id}`}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className="text-sm text-gray-600">{roleLabel(m.role)}</span>
                      )}
                      {canEditThisRow && (
                        <button
                          onClick={() => handleRemoveMember(m.id)}
                          data-testid={`member-remove-${m.id}`}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notification toggles — visible to Owner/Admin only.
                      Members can also change their own from Settings, and
                      the last write wins (member can override admin). */}
                  {canManage && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!m.daily_digest_enabled}
                          onChange={(e) => handleToggleNotification(m.id, 'daily_digest_enabled', e.target.checked)}
                          data-testid={`member-toggle-daily-${m.id}`}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">📨 Daily digest</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!m.weekly_digest_enabled}
                          onChange={(e) => handleToggleNotification(m.id, 'weekly_digest_enabled', e.target.checked)}
                          data-testid={`member-toggle-weekly-${m.id}`}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">📅 Weekly digest</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={m.task_reminders_enabled !== false}
                          onChange={(e) => handleToggleNotification(m.id, 'task_reminders_enabled', e.target.checked)}
                          data-testid={`member-toggle-reminders-${m.id}`}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-700">⏰ Task reminders</span>
                      </label>
                      {m.role === 'member' && (
                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="Off: they only see the business contacts they added">
                          <input
                            type="checkbox"
                            checked={m.can_view_contacts !== false}
                            onChange={(e) => handleToggleContactsAccess(m.id, e.target.checked)}
                            data-testid={`member-toggle-contacts-${m.id}`}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-gray-700">👥 Sees all business contacts</span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {canManage && invitations.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Pending invitations <span className="text-sm text-gray-500 font-normal">· {invitations.length}</span>
            </h2>
            <div className="divide-y divide-gray-100">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 py-3" data-testid={`invitation-row-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{inv.email}</p>
                    <p className="text-sm text-gray-500">
                      Invited as {roleLabel(inv.role)} · expires{' '}
                      {new Date(inv.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      onClick={() => handleResend(inv.id)}
                      data-testid={`invitation-resend-${inv.id}`}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleRevoke(inv.id)}
                      data-testid={`invitation-revoke-${inv.id}`}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </OrganizationLayout>
  );
};

export default OrganizationMembersPage;
